import { useState, useEffect, useCallback } from 'react';
import {
  leaveApplicationApi,
  STATUS_OPTIONS,
} from '../api/leaveApplicationApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { executePrintReport } from '../../../../utils/printReportUtil';
import { useExportJob } from '../../../../hooks/useExportJob';

const REPORT_COLUMNS = [
  { key: 'employee', label: 'Employee Name' },
  { key: 'employeeId', label: 'Employee Code' },
  { key: 'applyDateDisplay', label: 'Apply Date' },
  { key: 'leaveDateDisplay', label: 'Leave Date' },
  { key: 'days', label: 'Days', align: 'right' },
  { key: 'type', label: 'Day Type' },
  { key: 'leaveType', label: 'Leave Type' },
  { key: 'reason', label: 'Reason' },
  { key: 'remarks', label: 'Remarks' },
  { key: 'status', label: 'Status' },
];

export function useLeaveApplicationList() {
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchText, setSearchText] = useState('');

  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Dropdown options
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);

  // Load dropdown options once
  useEffect(() => {
    leaveApplicationApi.getOptions().then((opts) => {
      setEmployeeOptions([
        { value: '', label: 'All Employees' },
        ...opts.employees,
      ]);
      setLeaveTypeOptions([
        { value: '', label: 'All Leave Types' },
        ...opts.leaveTypes,
      ]);
    }).catch(() => {});
  }, []);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await leaveApplicationApi.getApplications({
        employeeId: employeeFilter,
        leaveTypeId: leaveTypeFilter,
        status: statusFilter,
        fromDate,
        toDate,
        searchText,
        page,
        pageSize,
      });
      setRecords(data.items || []);
      setTotalCount(data.total_count || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      appToast.error('Failed to load Leave Applications.');
    } finally {
      setIsLoading(false);
    }
  }, [employeeFilter, leaveTypeFilter, statusFilter, fromDate, toDate, searchText, page, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setEmployeeFilter('');
    setLeaveTypeFilter('');
    setStatusFilter('Pending');
    setFromDate('');
    setToDate('');
    setSearchText('');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const handleApprove = async (id, employee) => {
    const confirmed = await appConfirm({
      title: 'Approve Leave Application',
      message: `Are you sure you want to approve leave application for "${employee}"?`,
      confirmText: 'Approve',
      confirmVariant: 'primary',
    });
    if (!confirmed) return;

    try {
      await leaveApplicationApi.approveApplication(id, 1, 'Approved by manager');
      appToast.success(`Leave application for ${employee} approved successfully!`);
      loadRecords();
    } catch (err) {
      const errMsg = err?.response?.data?.detail || 'Failed to approve leave application.';
      appToast.error(errMsg);
    }
  };

  const handleReject = async (id, employee) => {
    const confirmed = await appConfirm({
      title: 'Reject Leave Application',
      message: `Are you sure you want to reject leave application for "${employee}"?`,
      confirmText: 'Reject',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    try {
      await leaveApplicationApi.approveApplication(id, 2, 'Rejected by manager');
      appToast.success(`Leave application for ${employee} rejected.`);
      loadRecords();
    } catch (err) {
      const errMsg = err?.response?.data?.detail || 'Failed to reject leave application.';
      appToast.error(errMsg);
    }
  };

  const handleDelete = async (id, employee) => {
    const confirmed = await appConfirm({
      title: 'Delete Leave Application',
      message: `Are you sure you want to delete leave application for "${employee}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    try {
      await leaveApplicationApi.deleteApplication(id);
      appToast.success(`Leave application deleted successfully.`);
      loadRecords();
    } catch (err) {
      const errMsg = err?.response?.data?.detail || 'Failed to delete leave application.';
      appToast.error(errMsg);
    }
  };

  const { triggerExport, isExporting } = useExportJob({
    module: 'leave-application',
    jobType: 'EXPORT',
  });

  const tools = [
    { key: 'print', label: 'Print Report', icon: 'print' },
    { key: 'export_excel', label: 'Export Excel', icon: 'description' },
    { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
  ];

  const handleToolSelect = (toolKey) => {
    const activeFilters = {
      search: searchText || undefined,
      status: statusFilter,
      leave_type_id: leaveTypeFilter ? Number(leaveTypeFilter) : undefined,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
    };

    if (toolKey === 'print') {
      executePrintReport({
        title: 'Leave Applications Report',
        columns: REPORT_COLUMNS,
        metadata: {
          'Status': statusFilter || 'All',
          'Date Range': fromDate && toDate ? `${fromDate} to ${toDate}` : 'All',
          'Search': searchText || 'All',
        },
        fetchReportData: async () => {
          const res = await leaveApplicationApi.getApplications({
            ...activeFilters,
            page: 1,
            pageSize: 1000,
          });
          return { items: res?.items || [] };
        },
        threshold: 1000,
        onExportPdf: () => triggerExport('pdf', activeFilters),
        onExportExcel: () => triggerExport('xlsx', activeFilters),
      });
    } else if (toolKey === 'export_excel') {
      triggerExport('xlsx', activeFilters);
    } else if (toolKey === 'export_pdf') {
      triggerExport('pdf', activeFilters);
    }
  };

  return {
    state: {
      records,
      isLoading,
      isExporting,
      employeeFilter,
      leaveTypeFilter,
      statusFilter,
      fromDate,
      toDate,
      searchText,
      page,
      pageSize,
      totalCount,
      totalPages,
      employeeOptions,
      leaveTypeOptions,
      statusOptions: STATUS_OPTIONS,
      tools,
    },
    actions: {
      setEmployeeFilter,
      setLeaveTypeFilter,
      setStatusFilter,
      setFromDate,
      setToDate,
      setSearchText,
      handleStatusTabChange,
      setPage,
      handleSearch,
      resetFilters,
      handleApprove,
      handleReject,
      handleDelete,
      handleToolSelect,
      refresh: loadRecords,
    },
  };
}
